/**
 * Shared markdown renderer with Mantine styling.
 *
 * Uses react-markdown with remark-gfm for GitHub Flavored Markdown support
 * (tables, strikethrough, task lists). Maps markdown elements to Mantine
 * components for consistent styling.
 */
import { Anchor, Blockquote, Code, Text } from '@mantine/core';
import cx from 'clsx';
import { type ReactNode, useDeferredValue } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import classes from './MarkdownRenderer.module.css';
import { markdownHeadingElements, markdownTableElements } from './markdown-components.tsx';

const remarkPlugins = [remarkGfm];

/**
 * Component mapping from markdown elements to Mantine components.
 * Provides consistent styling across the application.
 */
const components = {
  ...markdownTableElements,
  ...markdownHeadingElements,

  // Block elements
  p({ children }: { children?: ReactNode }) {
    return <Text component="p">{children}</Text>;
  },
  blockquote({ children }: { children?: ReactNode }) {
    return <Blockquote>{children}</Blockquote>;
  },
  hr() {
    // Use native hr element for semantic correctness (tests expect <hr>)
    return (
      <hr
        style={{
          margin: 'var(--mantine-spacing-md) 0',
          border: 'none',
          borderTop: '1px solid var(--mantine-color-default-border)',
        }}
      />
    );
  },

  // Code
  code({ children, className }: { children?: ReactNode; className?: string }) {
    // Inline code vs code blocks: pre > code has className with language
    const isBlock = className?.startsWith('language-');
    if (isBlock) {
      return <code className={className}>{children}</code>;
    }
    return <Code>{children}</Code>;
  },
  pre({ children }: { children?: ReactNode }) {
    return (
      <Code block className={classes.codeBlock}>
        {children}
      </Code>
    );
  },

  // Links
  a({ children, href }: { children?: ReactNode; href?: string }) {
    return (
      <Anchor href={href} rel="noopener noreferrer" target="_blank">
        {children}
      </Anchor>
    );
  },

  // Lists - use native elements for proper react-markdown integration
  // Mantine List requires specific structure that conflicts with markdown
  ul({ children }: { children?: ReactNode }) {
    return <ul>{children}</ul>;
  },
  ol({ children }: { children?: ReactNode }) {
    return <ol>{children}</ol>;
  },
  li({ children }: { children?: ReactNode }) {
    return <li>{children}</li>;
  },
};

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * Renders markdown content with Mantine-styled components.
 *
 * Supports GitHub Flavored Markdown including tables, strikethrough,
 * task lists, and autolinks.
 */
export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  const deferredContent = useDeferredValue(content);
  return (
    <div className={cx(classes.markdown, className)}>
      <ReactMarkdown components={components} remarkPlugins={remarkPlugins}>
        {deferredContent}
      </ReactMarkdown>
    </div>
  );
}
